import { ArrowRight } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { FieldInput } from "@/components/post-load/PostLoadCard";
import { CommentChipRow } from "@/components/post-load/EquipmentDetailsCard";
import { HANDLING_OPTIONS } from "@/components/lr-direct/DirectPublishCard";
import { cn } from "@/lib/utils";
import type { WizardFields } from "./wizardTypes";

/**
 * Guided wizard — STEP 5: ANYTHING ELSE? (optional, skippable).
 *
 * Commodity, reference #, comments with the form's handling/comment chips
 * (CommentChipRow + HANDLING_OPTIONS — the same sets the form uses), and a
 * note to brokers. Everything is optional; Skip jumps straight to Review.
 */

interface WizardExtrasStepProps {
  fields: WizardFields;
  onChange: (patch: Partial<WizardFields>) => void;
  onSkip: () => void;
}

export default function WizardExtrasStep({ fields, onChange, onSkip }: WizardExtrasStepProps) {
  const toggleHandling = (req: string) => {
    onChange({
      handlingRequirements: fields.handlingRequirements.includes(req)
        ? fields.handlingRequirements.filter((r) => r !== req)
        : [...fields.handlingRequirements, req],
    });
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <FieldInput
          label="Commodity"
          value={fields.commodity}
          onChange={(v) => onChange({ commodity: v })}
          placeholder="e.g. Paper products"
        />
        <FieldInput
          label="Reference #"
          value={fields.referenceNumber}
          onChange={(v) => onChange({ referenceNumber: v })}
          placeholder="PO / load # (optional)"
        />
      </div>

      <div>
        <label className="text-[11px] font-medium text-muted-foreground mb-1 block">Comments</label>
        <Textarea
          placeholder="Anything brokers should know — tap a chip to add it"
          className="text-xs min-h-[64px] resize-none"
          value={fields.comments}
          onChange={(e) => onChange({ comments: e.target.value })}
        />
        <div className="mt-1.5">
          <CommentChipRow comments={fields.comments} onCommentsChange={(v) => onChange({ comments: v })} />
        </div>
      </div>

      <div>
        <label className="text-[11px] font-medium text-muted-foreground mb-1 block">Handling requirements</label>
        <div className="flex items-center gap-1 flex-wrap">
          {HANDLING_OPTIONS.map((req) => {
            const active = fields.handlingRequirements.includes(req);
            return (
              <button
                key={req}
                type="button"
                onClick={() => toggleHandling(req)}
                aria-pressed={active}
                className={cn(
                  "inline-flex items-center rounded-full px-2 py-0.5 text-[9px] font-medium transition-colors duration-150 border",
                  active
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-border/40 bg-transparent text-muted-foreground hover:bg-muted/50"
                )}
              >
                {req}
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <label className="text-[11px] font-medium text-muted-foreground mb-1 block">Note to brokers</label>
        <Textarea
          placeholder="Shown to brokers alongside your post (optional)"
          className="text-xs min-h-[56px] resize-none"
          value={fields.shipperNotes}
          onChange={(e) => onChange({ shipperNotes: e.target.value })}
        />
      </div>

      <div className="flex justify-center pt-1">
        <button
          type="button"
          onClick={onSkip}
          className="inline-flex items-center gap-1 text-[11px] font-medium text-muted-foreground hover:text-foreground underline underline-offset-2 transition-colors"
        >
          Skip — nothing else to add
          <ArrowRight className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
